import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, ShoppingCart, ArrowRight, Calendar } from "lucide-react";
import { useUser } from "@/context/useUser";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { cn } from "@/lib/utils"; // Pastikan path cn sesuai dengan project Anda
import { format } from "date-fns";
import { id, enUS } from "date-fns/locale"; // 1. Impor locale enUS
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // 2. Impor hook i18n
import { usePackageTree } from "@/hooks/usePackageTree";
import { useRegistrationStep } from "@/hooks/useRegistrationStep";
import { useMemo } from "react";

interface WelcomeCardProps {
    currentStep?: 1 | 2 | 3 | 4; // 1: Registrasi, 2: Pilih Paket, 3: Pembayaran, 4: Unduh Kartu
}

const WelcomeCard: React.FC<WelcomeCardProps> = ({ currentStep: propStep }) => {
    const { t, i18n } = useTranslation('private'); // 3. Gunakan namespace private
    const navigate = useNavigate();
    const { data: user, isLoading: userLoading } = useUser();
    const { data: activePeriod, isLoading: periodLoading } = useActivePeriod();
    const { data: registrationStepData, isLoading: stepLoading } = useRegistrationStep(user?.participant_id, activePeriod?.id);

    const isLoading = userLoading || periodLoading || stepLoading;

    // Hanya Administrator (1), Pendaftar (2), dan Pendaftar Kolektif (3) yang
    // benar-benar punya keperluan memilih paket/melihat progress registrasi.
    // Whitelist (bukan blacklist) supaya role baru yang ditambahkan nanti
    // otomatis tidak melihat info ini kecuali sengaja ditambahkan ke daftar.
    const showPackageInfo = Boolean(user) && [1, 2, 3].includes(user!.role_id);

    // ✅ LOGIKA MAPPING STEP (Backend 1-5 ke Visual 1-4)
    // Backend Step: 1(Reg), 2(Paket), 3(Konf), 4(Bayar), 5(Kartu)
    const currentStep = useMemo(() => {
        const backendStep = registrationStepData?.step || propStep || 1;
        if (backendStep >= 5) return 4;
        if (backendStep >= 4) return 3;
        if (backendStep >= 2) return 2;
        return 1;
    }, [registrationStepData?.step, propStep]);

    // 4. Sesuaikan format tanggal agar mengikuti bahasa i18next
    const formatDate = (date: string) =>
        format(new Date(date), "d MMMM yyyy", {
            locale: i18n.language === 'id' ? id : enUS
        });

    const { data: packages, isLoading: packagesLoading } = usePackageTree(activePeriod?.id, Boolean(activePeriod?.id));
    const hasActivePackages = Boolean(packages && packages.length > 0);
    const hasActivePeriod = Boolean(activePeriod);
    const canNavigateToPackage = hasActivePackages && hasActivePeriod;

    // Data Step Wizard
    const steps = [
        { id: 1, label: "Registrasi" },
        { id: 2, label: "Pilih Paket" },
        { id: 3, label: "Pembayaran" },
        { id: 4, label: "Unduh Kartu" },
    ];

    const getTwoWords = (fullName?: string) => {
        if (!fullName) return "User";
        const words = fullName.trim().split(/\s+/);
        if (words.length >= 2) {
            return `${words[0]} ${words[1]}`;
        }
        return words[0]; // Jika cuma 1 kata, kembalikan 1 kata
    };

    return (
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 md:p-10">
            {/* Dekorasi Background */}
            <div className="absolute top-0 right-0 h-48 w-48 bg-blue-600/5 rounded-full blur-[80px] -mr-20 -mt-20 pointer-events-none" />
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500" />

            <div className="relative z-10 flex flex-col gap-8">

                {/* Konten Utama */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="space-y-4">
                        {/* <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                <Compass className="w-5 h-5" />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-slate-400">
                                Dashboard Overview
                            </span>
                        </div> */}
                        <div className="space-y-1">
                            {isLoading ? (
                                <Skeleton className="h-8 w-64 bg-slate-200 dark:bg-slate-800" />
                            ) : (
                                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                                    {t('welcome_card.greeting')}, {getTwoWords(user?.name) ?? t('welcome_card.default_user')} <span className="text-blue-600">.</span>
                                </h1>
                            )}
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl leading-relaxed font-medium italic">
                                {t('welcome_card.welcome_message')}
                            </p>
                        </div>
                    </div>

                    <div className="hidden md:block">
                        <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 p-5 rounded-3xl flex items-center gap-4">
                            <div className="h-10 w-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-600">
                                <CheckCircle2 className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('welcome_card.account_status')}</p>
                                <p className="text-xs font-bold text-slate-900 dark:text-white">{t('welcome_card.verified')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {showPackageInfo && (
                <div className="relative group overflow-hidden rounded-[2rem] bg-gradient-to-r from-blue-600 to-indigo-700 p-1 shadow-lg shadow-blue-500/20">
                    <div className="relative bg-white dark:bg-slate-900 rounded-[1.9rem] p-6">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600">
                                    <ShoppingCart className="w-6 h-6" />
                                </div>
                                <div>
                                    {packagesLoading ? (
                                        <>
                                            <Skeleton className="h-5 w-40 bg-slate-200 dark:bg-slate-800" />
                                            <Skeleton className="h-3 w-64 bg-slate-200 dark:bg-slate-800 mt-2" />
                                        </>
                                    ) : (
                                        <>
                                            <h3 className="font-bold text-slate-900 dark:text-white">{hasActivePackages && activePeriod ? `Pilih Paket - ${activePeriod.name}` : "Tidak ada paket aktif"}</h3>
                                            <p className="text-xs text-slate-500">{hasActivePackages ? "Selesaikan registrasi dengan memilih paket kegiatan." : "Saat ini belum ada paket aktif untuk periode ini."}</p>
                                            {hasActivePackages && activePeriod && (
                                                <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500">
                                                    <span className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 px-0 py-1.5 rounded-xl">
                                                        <Calendar className="w-3.5 h-3.5 text-blue-500" />
                                                        {formatDate(activePeriod.start_date)} - {formatDate(activePeriod.end_date)}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>


                            {packagesLoading ? (
                                <Skeleton className="h-9 w-28 rounded-xl" />
                            ) : canNavigateToPackage ? (
                                <button
                                    onClick={() => navigate("/order/paket")}
                                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-all"
                                >
                                    Pilih Paket <ArrowRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button className="px-6 py-2 bg-slate-200 text-slate-500 rounded-xl text-sm font-bold" disabled>
                                    Tidak Ada Paket
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                )}

                {/* ✅ STEP WIZARD (Progress Bar) */}
                {showPackageInfo && (
                <div className="flex items-center justify-between w-full max-w-2xl mx-auto mb-2">
                    {steps.map((step, index) => (
                        <div key={step.id} className="flex-1 flex items-center">
                            <div className="flex flex-col items-center">
                                <div className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-300",
                                    currentStep >= step.id
                                        ? "bg-blue-600 text-white"
                                        : "bg-slate-100 text-slate-400 dark:bg-slate-800"
                                )}>
                                    {currentStep > step.id ? <CheckCircle2 className="w-5 h-5" /> : step.id}
                                </div>
                                <span className={cn(
                                    "text-[10px] font-bold uppercase mt-2",
                                    currentStep >= step.id ? "text-blue-600" : "text-slate-400"
                                )}>{step.label}</span>
                            </div>
                            {index < steps.length - 1 && (
                                <div className={cn(
                                    "flex-1 h-0.5 mx-2 -mt-6",
                                    currentStep > step.id ? "bg-blue-600" : "bg-slate-100 dark:bg-slate-800"
                                )} />
                            )}
                        </div>
                    ))}
                </div>
                )}
            </div>
        </div>
    );
};

export default WelcomeCard;